import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import useAuth from "../../hooks/useAuth";
import { Formik } from "formik";
import * as Yup from "yup";
import api from "../../api";
import ElementRootFields from "../../components/fields/ElementRootFields";
import ElementCustomFields from "../../components/fields/ElementCustomFields";
import ElementDefaultFields from "../../components/fields/ElementDefaultFields";
import { useNavigate, NavLink } from "react-router-dom";
import { fieldNameToAssetName } from "../../components/fields/fieldsHelper";
import ChooseAccessProfile from "../people/ChooseAccessProfile";

const NewUser = ({ title, show, handleClose, onSuccess, ...rest }) => {
  const apiName = "people";
  const navigate = useNavigate();
  const [curPermission, setCurPermission] = useState(null);
  const { user } = useAuth();
  const [customFieldConfig, setCustomFieldConfig] = useState(null);
  const [schema, setSchema] = useState({});
  const [showAccess, setShowAccess] = useState(false);
  // const [showDelete, setShowDelete] = useState(false);
  let sendType = null;

  const handleChooseAccessProfile = async (accessProfile) => {
    // await linkPermission(accessProfile.id);
    setCurPermission(accessProfile);
    setShowAccess(false);
  };

  const handleRemoveAccess = () => {
    setCurPermission(null);
  };

  useEffect(() => {
    if (user && apiName) {
      if (user.role === "Root") {
        // Not used as Root shouldn't create elements
        // api.get(`${apiName || "assets"}/customfieldconfig/0`).then((res) => {
        //   setCustomFieldConfig(res.data);
        // });
      } else {
        setCustomFieldConfig(user.personFieldConfig);
      }
    }
  }, [user, apiName]);

  useEffect(() => {
    if (!customFieldConfig) return;

    setSchema(
      customFieldConfig.customFields.reduce(
        (previous, value) => {
          let s = Yup.string()
            .max(255)
            .matches(
              /^[^#]*$/,
              "'#' is a protected character used for hashtag searches and cannot be used here"
            );
          if (value.mandatory) {
            s = s.required(value.displayName + " is required");
          }
          let propertyName = fieldNameToAssetName(value.fieldName);
          // previous = { ...previous};
          previous[propertyName] = s;
          return previous;
        },
        {
          email: Yup.string().email("Invalid email"),
        }
      )
    );
  }, [customFieldConfig]);

  return (
    <React.Fragment>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            enableReinitialize
            initialValues={{
              submit: false,
              primaryName: "",
              secondaryName: "",
              reference: "",
              fleetNumber: "",
              category: "",
              suppString1: "",
              suppString2: "",
              suppString3: "",
              suppString4: "",
              suppString5: "",
              email: "",
              create: false,
              edit: false,
              delete: false,
              permissionId: "",
            }}
            validationSchema={Yup.object().shape(schema)}
            onSubmit={async (
              values,
              { setErrors, setStatus, setSubmitting }
            ) => {
              var res;

              try {
                res = await api.post(apiName, {
                  primaryName: values.primaryName,
                  secondaryName: values.secondaryName,
                  reference: values.reference,
                  fleetNumber: values.fleetNumber,
                  category: values.category,
                  suppString1: values.suppString1,
                  suppString2: values.suppString2,
                  suppString3: values.suppString3,
                  suppString4: values.suppString4,
                  suppString5: values.suppString5,
                });

                await api.put("accounts/user", {
                  id: res.data.id,
                  email: values.email,
                  create: values.create,
                  edit: values.edit,
                  delete: values.delete,
                  permissionId: curPermission ? curPermission.id : null,
                  sendemail: sendType === 1,
                });
              } catch (error) {
                const message = error.message || "Something went wrong";
                setStatus({ success: false });
                setErrors({ submit: message });
                setSubmitting(false);
              }

              if (res?.data?.id) {
                if (onSuccess) {
                  onSuccess(res.data.id);
                } else {
                  navigate(`/users/edit?id=${res.data.id}`);
                }
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
            }) => (
              <Form onSubmit={handleSubmit}>
                {errors.submit && (
                  <Alert className="my-3" variant="danger">
                    <div className="alert-message">{errors.submit}</div>
                  </Alert>
                )}

                {customFieldConfig && customFieldConfig.enabled ? (
                  user.role === "Root" ? (
                    <ElementRootFields
                      apiName={apiName}
                      customFieldConfig={customFieldConfig}
                      mode={"new"}
                      {...rest}
                    />
                  ) : (
                    <ElementCustomFields
                      apiName={apiName}
                      customFieldConfig={customFieldConfig}
                      mode={"new"}
                      {...rest}
                    />
                  )
                ) : (
                  <ElementDefaultFields
                    apiName={apiName}
                    mode={"new"}
                    {...rest}
                  />
                )}

                <Form.Group className="mb-3">
                  <Form.Label>
                    <span className="me-1">Access Profile</span>
                  </Form.Label>
                  <div>
                    {curPermission && (
                      <NavLink
                        to={{
                          pathname: curPermission
                            ? `/accessprofiles/edit`
                            : "/accessprofiles",
                          search: curPermission
                            ? `?id=${curPermission.id}`
                            : "",
                        }}
                      >
                        {curPermission?.permissionName}
                      </NavLink>
                    )}
                    {!curPermission && <span>No Access</span>}
                  </div>
                  <div className="mt-2">
                    <Button onClick={() => setShowAccess(true)}>
                      Choose Access
                    </Button>
                    {curPermission && (
                      <Button
                        onClick={handleRemoveAccess}
                        className="ms-2"
                        variant={"danger"}
                      >
                        Remove Access
                      </Button>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    size="lg"
                    type="text"
                    name="email"
                    value={values.email}
                    isInvalid={Boolean(touched.email && errors.email)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  {!!touched.email && (
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                {user?.globalCrud && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        name="create"
                        label="Create"
                        checked={values.create}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        name="edit"
                        label="Edit"
                        checked={values.edit}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        name="delete"
                        label="Delete"
                        checked={values.delete}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </>
                )}

                <div className="mt-3">
                  <Button
                    className="me-2"
                    disabled={isSubmitting}
                    type="submit"
                    onClick={() => (sendType = 1)}
                  >
                    {"Save and send registration email"}
                  </Button>

                  <Button
                    disabled={isSubmitting}
                    type="submit"
                    onClick={() => (sendType = 2)}
                    variant="success"
                  >
                    Save
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
      <ChooseAccessProfile
        show={showAccess}
        setShow={setShowAccess}
        currentlyChosenId={curPermission?.id}
        onChoose={handleChooseAccessProfile}
      />
    </React.Fragment>
  );
};

export default NewUser;
